import {
  IAPIBody_postContact,
  IApiBody_postDisablePosition,
  IAPIBody_postInternalOrder,
  IAPIBody_postOrder,
  IAPIBody_postPayment,
  IApiBody_postPosition,
  IAPIBody_postPreorder,
  IAPIError,
  IAPIError_getData,
  IAPIError_getMultiple,
  IAPIError_getOrders,
  IAPIError_getSettings,
  IAPIError_postContact,
  IAPIError_postInternalOrder,
  IAPIError_postOrder,
  IAPIError_postPayment,
  IAPIError_updateCount,
  IAPIPathPrefixed,
  IAPIResponse,
  IAPIResponse_getData,
  IAPIResponse_getMultiple,
  IAPIResponse_getOrders,
  IAPIResponse_getSettings,
  IAPIResponse_postContact,
  IAPIResponse_postDisablePosition,
  IAPIResponse_postInternalOrder,
  IAPIResponse_postOrder,
  IAPIResponse_postPayment,
  IAPIResponse_postPosition,
  IAPIResponse_updateCount,
} from 'my-poppies-b2b-types';
import { sleep } from 'sleep-lightweight';

import { IApiConfig } from '@/api/IAPIConfig';
import { IApiFnMocked } from '@/api/IApiFnMocked';
import { IAPIMethod } from '@/api/IAPIMethod';
import { EnvVarManager } from '@/env/envVarManager';
import { getCookieValue } from '@/utils/getCookieValue';
import { getObjectKeys } from '@/utils/getObjectKeys';

const baseUrl = EnvVarManager.getEnvVar('NEXT_PUBLIC_BACKEND_BASE_URL');

export type ICallApiFn = (
  method: IAPIMethod,
  path: IAPIPathPrefixed,
  config: IApiConfig,
  mockFn: IApiFnMocked
) => Promise<IAPIResponse | IAPIError>;

export async function callApi(
  method: 'get',
  path: '/settings',
  config: IApiConfig,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_getSettings | IAPIError_getSettings>;

export async function callApi(
  method: 'get',
  path: '/data',
  config: IApiConfig,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_getData | IAPIError_getData>;

export async function callApi(
  method: 'post',
  path: '/internalOrder',
  config: IApiConfig<IAPIBody_postInternalOrder>,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_postInternalOrder | IAPIError_postInternalOrder>;

export async function callApi(
  method: 'post',
  path: '/contact',
  config: IApiConfig<IAPIBody_postContact>,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_postContact | IAPIError_postContact>;

export async function callApi(
  method: 'post',
  path: '/payment',
  config: IApiConfig<IAPIBody_postPayment>,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_postPayment | IAPIError_postPayment>;

export async function callApi(
  method: 'get',
  path: '/orders',
  config: IApiConfig,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_getOrders | IAPIError_getOrders>;

export async function callApi<X extends object>(
  method: 'get',
  path: '/contacts',
  config: IApiConfig,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_getMultiple<X> | IAPIError_getMultiple>;

export async function callApi(
  method: 'get',
  path: '/update-count',
  config: IApiConfig,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_updateCount | IAPIError_updateCount>;

export async function callApi(
  method: 'post',
  path: '/order',
  config: IApiConfig<IAPIBody_postOrder | IAPIBody_postPreorder>,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_postOrder | IAPIError_postOrder>;

export async function callApi(
  method: 'post',
  path: '/position',
  config: IApiConfig<IApiBody_postPosition>,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_postPosition>;

export async function callApi(
  method: 'post',
  path: '/position',
  config: IApiConfig<IApiBody_postDisablePosition>,
  mockFn: IApiFnMocked
): Promise<IAPIResponse_postDisablePosition>;

export async function callApi(
  method: IAPIMethod,
  path: IAPIPathPrefixed,
  config: IApiConfig,
  mockFn: IApiFnMocked,
  logoutFn?: () => Promise<void>,
  mockApi?: boolean
): Promise<IAPIResponse | IAPIError> {
  if (mockApi) {
    return await mockFn();
  }

  const url = config.isExternal ? new URL(path) : new URL(`${baseUrl}${path}`);

  try {
    const headers = new Headers();

    const hankoCookieValue = getCookieValue('hanko');
    if (!hankoCookieValue) {
      logoutFn?.();
      throw new Error('No hanko cookie found');
    }
    headers.append('Hto', hankoCookieValue);
    if (config.body) {
      headers.append('Content-Type', 'application/json');
    }
    if (config.params) {
      const params = new URLSearchParams();
      getObjectKeys(config.params).forEach((key) => {
        if (config.params![key] === undefined) {
          return;
        }
        params.append(key, encodeURIComponent(config.params![key]));
      });
      url.search = params.toString();
    }
    const resRaw = await fetch(url, {
      cache: 'no-store',
      method,
      headers,
      body: config.body ? JSON.stringify(config.body) : undefined,
    });
    if (resRaw.status >= 400) {
      throw new Error(`Status code is ${resRaw.status}`);
    }
    if (config.afterSuccessSleep !== undefined) {
      await sleep(config.afterSuccessSleep);
    }
    if (resRaw.status === 204) {
      return {};
    }
    return await resRaw.json();
  } catch (e) {
    const errorMsg = `callApi call ${method} ${url} failed with status ${e}`;
    console.error(errorMsg);
    // re-throw:
    // @ts-expect-error ignore
    throw new Error(e);
  }
}
